import { Controller } from 'react-hook-form'
import MuiCheckbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel'
import GridItem, { GridItemProps, omitGridProps } from './GridItem'

export interface CheckboxProps<T = boolean> extends GridItemProps {
    name?: string
    label?: string
    control?: FormControlLabelProps['control']
    disabled?: boolean
    onClick?: React.MouseEventHandler
    transform?: {
        displayAs: (value: T) => boolean
        storeAs: (value: boolean) => T
    }
}

/**
 * Checkbox for forms using React Hook Form
 * @param props {
 *   name?: if we use it as RHF field - the name for the form
 *   label?: the label to show
 *   control?: if we don't want to have the value in RHF state we have to define our own control to display it
 *   transform?: convert between stored value T and boolean for display
 * }
 */
const Checkbox = <T = boolean,>(props: CheckboxProps<T>) => {
    const { transform, ...restProps } = props
    return (
        <GridItem {...restProps}>
            <FormControl margin="normal">
                {restProps.control ? (
                    <FormControlLabel
                        onClick={restProps.onClick}
                        control={restProps.control}
                        label={restProps.label || restProps.name}
                    />
                ) : (
                    <Controller
                        name={restProps.name!}
                        render={({ field: { ref, ...field } }) => (
                            <FormControlLabel
                                control={
                                    <MuiCheckbox
                                        slotProps={{ input: { ref } }}
                                        checked={
                                            transform
                                                ? transform.displayAs(field.value as T)
                                                : (field.value as boolean) || false
                                        }
                                        {...field}
                                        onChange={
                                            transform
                                                ? (_, checked) => field.onChange(transform.storeAs(checked))
                                                : field.onChange
                                        }
                                        {...omitGridProps(restProps)}
                                    />
                                }
                                label={restProps.label || restProps.name}
                            />
                        )}
                    />
                )}
            </FormControl>
        </GridItem>
    )
}

export default Checkbox
